import React, { useState } from "react";
import { Box, Paper, Typography, Button, Grid, Snackbar, Alert } from "@mui/material";
import UploadRoundedIcon from "@mui/icons-material/UploadRounded";

import apiClient, { resolveFileUrl, useApiQuery } from "../api/client";

const DOC_CONFIG = [
  { key: "brochure", label: "Brochure PDF", accept: "application/pdf" },
  {
    key: "floorplan",
    label: "Floor Plan (PDF or Image)",
    accept: "application/pdf,image/jpeg,image/png,image/webp",
  },
  {
    key: "elevation",
    label: "Elevation Image",
    accept: "image/jpeg,image/png,image/webp",
  },
];

const DocumentCard = ({ config, onToast }) => {
  const { data, refetch } = useApiQuery(`/documents/${config.key}`);
  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState(null);

  const isImage = data?.mimeType
    ? data.mimeType.startsWith("image/")
    : config.key === "elevation";

  const handleUpload = async () => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    setIsLoading(true);
    try {
      await apiClient.post(`/documents/${config.key}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFile(null);
      onToast(`${config.label} updated.`);
      refetch();
    } catch (err) {
      onToast(err?.response?.data?.message || `Failed to upload ${config.label}.`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        {config.label}
      </Typography>

      {data?.fileUrl ? (
        isImage ? (
          <Box
            component="img"
            src={resolveFileUrl(data.fileUrl)}
            alt={config.label}
            sx={{ width: "100%", maxHeight: 160, objectFit: "cover", borderRadius: 1, mb: 2 }}
          />
        ) : (
          <Typography variant="body2" sx={{ mb: 2 }}>
            <a href={resolveFileUrl(data.fileUrl)} target="_blank" rel="noreferrer">
              View current file
            </a>
          </Typography>
        )
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No file uploaded yet.
        </Typography>
      )}

      <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
        <Button component="label" startIcon={<UploadRoundedIcon />} variant="outlined">
          {file ? file.name : "Choose File"}
          <input
            type="file"
            accept={config.accept}
            hidden
            onChange={(e) => setFile(e.target.files[0])}
          />
        </Button>
        <Button variant="contained" onClick={handleUpload} disabled={!file || isLoading}>
          {isLoading ? "Uploading…" : "Upload / Replace"}
        </Button>
      </Box>
    </Paper>
  );
};

const AdminDocuments = () => {
  const [toast, setToast] = useState("");

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Documents
      </Typography>

      <Grid container spacing={3}>
        {DOC_CONFIG.map((cfg) => (
          <Grid item xs={12} md={4} key={cfg.key}>
            <DocumentCard config={cfg} onToast={setToast} />
          </Grid>
        ))}
      </Grid>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast("")}>
        <Alert severity="success" onClose={() => setToast("")}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminDocuments;
